import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";

/* ----------------------------------------------------------------
   Componenti interni
------------------------------------------------------------------*/
import OrdersFilterBar from "./components/orders/OrdersFilterBar";
import OrdersTable from "./components/orders/OrdersTable";
import InProgressTablesView from "./components/orders/InProgressTablesView";
import OpenTablesView from "./components/orders/OpenTablesView";

/* ----------------------------------------------------------------
   Config
------------------------------------------------------------------*/
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/* =================================================================
                           COMPONENT
==================================================================*/
export default function OrdersPage() {
  /* -----------------------------
     Tab principale: "pos" | "kitchen" | "history"
  -----------------------------*/
  const [currentView, setCurrentView] = useState("pos");

  /* -----------------------------
     Restaurants (filter)
  -----------------------------*/
  const [restaurantsList, setRestaurantsList]   = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState("all");

  /* -----------------------------
     Date filter per lo storico
  -----------------------------*/
  const now          = new Date();
  const todayStr     = now.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate,   setEndDate]   = useState(todayStr);

  /* -----------------------------
     Ordini “in corso” per la cucina
  -----------------------------*/
  const [kitchenOrders, setKitchenOrders] = useState([]);

  /* -----------------------------
     Rilevazione mobile
  -----------------------------*/
  const isMobile = useMediaQuery({ maxWidth: 768 });

  /* -----------------------------
     WebSocket
  -----------------------------*/
  const wsRef = useRef(null);

  /* =====================================================
     EFFETTI
  ===================================================== */

  /* Al mount: carichiamo la lista ristoranti */
  useEffect(() => {
    fetchRestaurantsList();
  }, []);

  /* WebSocket per aggiornamenti in tempo reale */
  useEffect(() => {
    const ws = new WebSocket(`${process.env.REACT_APP_WS_URL}/ws`);
    ws.onopen    = () => console.log("WS Admin: connesso");
    ws.onmessage = (evt) => {
      console.log("WS Admin: messaggio:", evt.data);
      if (evt.data === "reload_orders" && currentView === "kitchen") {
        fetchKitchenOrders();
      }
      // OpenTablesView e OrdersTable gestiscono già il refresh autonomamente
    };
    ws.onclose = () => console.log("WS Admin: chiuso");
    wsRef.current = ws;

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [currentView]);

  /* Se la tab è "kitchen", fetch ordini in corso */
  useEffect(() => {
    if (currentView === "kitchen") {
      fetchKitchenOrders();
    }
  }, [currentView, selectedRestaurant]);

  /* =====================================================
     FUNZIONI DI FETCH
  ===================================================== */
  const fetchRestaurantsList = async () => {
    try {
      const token = localStorage.getItem("access_token") || "";
      const url   = `${API_BASE_URL}/admin/kpi/restaurants_list`;
      const res   = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Errore ristoranti: ${res.status}`);
      const data  = await res.json();
      setRestaurantsList(data);
    } catch (err) {
      console.error("fetchRestaurantsList error:", err);
    }
  };

  const fetchKitchenOrders = async () => {
    try {
      const token = localStorage.getItem("access_token") || "";
      let url     = `${API_BASE_URL}/orders`;
      const params = [];

      if (selectedRestaurant !== "all") params.push(`restaurant_id=${selectedRestaurant}`);
      if (params.length) url += "?" + params.join("&");

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Errore fetchKitchenOrders");
      const data = await res.json();

      const rawOrders   = Array.isArray(data) ? data : data.orders || [];
      const inProgress  = rawOrders.filter((o) =>
        ["pending", "in_progress", "ready_to_serve"].includes(o.status)
      );
      setKitchenOrders(inProgress);
    } catch (err) {
      console.error("fetchKitchenOrders error:", err);
    }
  };

  /* =====================================================
     HANDLER FILTRO
  ===================================================== */
  const handleFilter = () => {
    if (currentView === "kitchen") {
      fetchKitchenOrders();
    }
    // POS e History si aggiornano da soli tramite props / useEffect interni
  };

  /* =====================================================
     RENDER
  ===================================================== */
  return (
    <div style={styles.pageContainer}>
      {/* Selettore vista: POS / Cucina / Storico */}
      <div style={styles.viewToggleContainer}>
        <button
          onClick={() => setCurrentView("pos")}
          style={{
            ...styles.toggleButton,
            backgroundColor: currentView === "pos" ? styles.colors.primary : "#f0f0f0",
            color:           currentView === "pos" ? "#fff"              : "#333",
          }}
        >
          POS
        </button>
        <button
          onClick={() => setCurrentView("kitchen")}
          style={{
            ...styles.toggleButton,
            backgroundColor: currentView === "kitchen" ? styles.colors.primary : "#f0f0f0",
            color:           currentView === "kitchen" ? "#fff"              : "#333",
          }}
        >
          Cucina
        </button>
        <button
          onClick={() => setCurrentView("history")}
          style={{
            ...styles.toggleButton,
            backgroundColor: currentView === "history" ? styles.colors.primary : "#f0f0f0",
            color:           currentView === "history" ? "#fff"              : "#333",
          }}
        >
          Storico
        </button>
      </div>

      {/* Barra dei filtri */}
      <OrdersFilterBar
        restaurantsList    ={restaurantsList}
        selectedRestaurant ={selectedRestaurant}
        onChangeRestaurant ={setSelectedRestaurant}
        onFilter           ={handleFilter}
        showDateFilters    ={currentView === "history"}
        startDate          ={startDate}
        endDate            ={endDate}
        onChangeStart      ={setStartDate}
        onChangeEnd        ={setEndDate}
      />

      {/* ============ VISTA POS ============ */}
      {currentView === "pos" && (
        <div style={styles.sectionCard}>
          <OpenTablesView selectedRestaurant={selectedRestaurant} />
        </div>
      )}

      {/* ============ VISTA KITCHEN ============ */}
      {currentView === "kitchen" && (
        <div style={styles.sectionCard}>
          <InProgressTablesView orders={kitchenOrders} isMobile={isMobile} />
        </div>
      )}

      {/* ============ VISTA STORICO (solo tabella) ============ */}
      {currentView === "history" && (
        <div style={styles.sectionCard}>
          <OrdersTable
            selectedRestaurant={selectedRestaurant}
            startDate={startDate}
            endDate={endDate}
            isMobile={isMobile}
          />
        </div>
      )}
    </div>
  );
}

/* =====================================================
   STILI
===================================================== */
const styles = {
  colors: {
    primary: "#336699",
  },
  pageContainer: {
    backgroundColor: "#F8F8F8",
    minHeight: "100vh",
    padding: "16px",
    fontSize: "16px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  viewToggleContainer: {
    display: "flex",
    gap: "1rem",
    margin: "1rem 0",
  },
  toggleButton: {
    flex: 1,
    border: "none",
    borderRadius: "8px",
    padding: "0.75rem",
    fontSize: "1rem",
    cursor: "pointer",
    fontWeight: "bold",
    transition: "background-color 0.25s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  sectionCard: {
    marginTop: "1rem",
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "1rem",
    boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
  },
};
